import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import type { HospitalDataContextType } from "@/contexts/HospitalDataProvider";
import { buildCostPrediction } from "@/utils/costPrediction";
import { formatEmployeeStatusReason } from "@/utils/employeeStatusReason";
import { deriveNurseStatusAtDate, formatPersonFullName, getNurseStatusEndDate, getNurseStatusReason } from "@/utils/formatters";
import {
	type BreakRulesConfig,
	type CostPredictionResponse,
	EffectiveEmployeeStatus,
	type Nurse,
	type NurseData,
	NurseFullRoleShort,
	NurseRole,
	type PanelShiftData,
	type Shift,
	type SpecificPreference,
	type WeekData,
} from "@/utils/models";
import {
	collectNurseShiftsAroundDay,
	isOnPtoDuringWindow,
	resolveShiftAvailability,
	SHIFT_OVERLAP_TOLERANCE_MS,
	type ShiftAvailability,
	type ShiftAvailabilityWindow,
	type ShiftSource,
} from "@/utils/shiftAvailability";
import { countDailyHours } from "@/utils/statsCalculations";
import { getEmploymentTypeDisplayName, getShiftWorkHours } from "@/utils/utilFunctions";
import { buildRowBadges, describeUnavailableReason } from "./addStaffUtils";

const STATUS_DATE_FORMAT = "MMM d, yyyy";

export type WeekStripArgs = {
	nurse: Nurse;
	dateRange: readonly DateTime[];
	/** The data-week's shift slots (`hospitalData.weeks[weekIndex].weeklyShifts`). */
	weeklyShifts: readonly Shift[];
	/** The nurse's own per-slot week (`filterNurseWeek` of the data-week). */
	nurseWeeklyShifts: readonly Shift[];
	shiftNo: number;
	departmentId: string;
	dayStart: string;
	/** The nurse's time off, from `useNurseTimeOff`. */
	timeOff: SpecificPreference[];
	getShiftTimes: HospitalDataContextType["getShiftTimes"];
	t: TFunction;
	/** The department's name-order flag (`useLastNameFirst()`). */
	lastNameFirst: boolean;
};

const hasLiveShift = (shift: Shift | undefined, index: number) => !!shift?.days[index].nurses.some((n) => !n.isPendingToDelete);

/** Day / night / off (PTO) for one day of the strip. */
const resolveDayShiftType = (args: WeekStripArgs, date: DateTime, index: number): "day" | "night" | "off" | null => {
	const { nurse, weeklyShifts, nurseWeeklyShifts, shiftNo, dayStart, timeOff, getShiftTimes } = args;
	if (hasLiveShift(nurseWeeklyShifts.at(0), index)) return "day";
	if (hasLiveShift(nurseWeeklyShifts.at(1), index)) return "night";
	const { start, end } = getShiftTimes(weeklyShifts[shiftNo].days[date.weekdayISO], shiftNo, nurse.id);
	// The same window-based PTO test as the row badge, against that day's slot hours.
	return isOnPtoDuringWindow(timeOff, { start, end }, dayStart) ? "off" : null;
};

const SHIFT_TYPE_TOOLTIP_KEYS = { day: "add.staff.week.day", night: "add.staff.week.night", off: "add.staff.week.off" } as const;

/** One day cell: the shift icon, or the pause marker (with the status detail) while On Leave / Suspended. */
const buildDayCell = (args: WeekStripArgs, date: DateTime, index: number): PanelShiftData => {
	const { nurse, departmentId, t, lastNameFirst } = args;
	const shiftType = resolveDayShiftType(args, date, index);
	// The strip marks no day: the weekly "avoid" preferences it used to aren't read on the staffing pages.
	const hasWarning = false;
	const status = deriveNurseStatusAtDate(nurse, departmentId, date);
	if (status === EffectiveEmployeeStatus.OnLeave || status === EffectiveEmployeeStatus.Suspended) {
		const unavailableStatus = status === EffectiveEmployeeStatus.Suspended ? "Suspended" : "OnLeave";
		const reason = formatEmployeeStatusReason(getNurseStatusReason(nurse, departmentId), t);
		const endDate = getNurseStatusEndDate(nurse, departmentId);
		const tooltip = [
			t(unavailableStatus === "Suspended" ? "employee.status.chip.suspended" : "employee.status.chip.on_leave"),
			endDate ? t("employee.status.chip.until", { date: endDate.toFormat(STATUS_DATE_FORMAT) }) : null,
			reason ? t("employee.status.chip.reason", { reason }) : null,
		]
			.filter(Boolean)
			.join(" · ");
		return { shiftType, tooltip, hasWarning, unavailableStatus };
	}
	const tooltip = shiftType ? t(SHIFT_TYPE_TOOLTIP_KEYS[shiftType], { name: formatPersonFullName(nurse, lastNameFirst) }) : "";
	return { shiftType, tooltip, hasWarning };
};

/** The nurse's week, one cell per date in the strip. */
export const buildWeekStrip = (args: WeekStripArgs): PanelShiftData[] => args.dateRange.map((date, index) => buildDayCell(args, date, index));

export type RowHoursArgs = {
	nurse: Nurse;
	window: ShiftAvailabilityWindow;
	departmentId: string;
	breakRulesConfig: BreakRulesConfig[];
	nurseWeeklyShifts: Shift[];
	dayNo: number;
};

/** Hours the nurse would work on the target day INCLUDING the new shift (paid hours, breaks deducted). */
export const computeDailyHoursWithShift = ({ nurse, window, departmentId, breakRulesConfig, nurseWeeklyShifts, dayNo }: RowHoursArgs): number => {
	const nurseRole = NurseRole[NurseFullRoleShort[nurse.role] as keyof typeof NurseRole];
	const shiftWorkHours = getShiftWorkHours(
		departmentId,
		window.end,
		window.start,
		breakRulesConfig,
		nurseRole,
		nurse.employmentType,
		undefined,
		undefined,
		nurse.union,
	);
	const existingHours = countDailyHours({
		nurseId: nurse.id,
		departmentId,
		weeklyShifts: nurseWeeklyShifts,
		dayIndex: dayNo,
		breakRulesConfig,
		nurseUnion: nurse.union,
	});
	return existingHours + shiftWorkHours;
};

export type NurseRowArgs = {
	nurse: Nurse;
	shifts: PanelShiftData[];
	cost: ReturnType<typeof buildCostPrediction>;
	dailyHours: number;
	availability: ShiftAvailability;
	t: TFunction;
	lastNameFirst: boolean;
};

/** The grid row: identity + week + cost, and either the "can't work" reason or the soft badges. */
export const buildNurseRow = ({ nurse, shifts, cost, dailyHours, availability, t, lastNameFirst }: NurseRowArgs): NurseData => ({
	id: nurse.id,
	name: formatPersonFullName(nurse, lastNameFirst),
	role: nurse.role,
	nurseType: getEmploymentTypeDisplayName(nurse.employmentType),
	overtimeHours: cost.overtimeHours,
	weeklyOvertimeHoursBeforeChange: cost.weeklyOvertimeHoursBeforeChange,
	predictedCost: cost.costDisplay,
	predictedCostValue: cost.costValue,
	dailyHours,
	shifts,
	charge: nurse.isShiftManager || nurse.canBeReliefCharge,
	selectable: true,
	trainingStatus: nurse.trainingStatus,
	...(nurse.nursingStartingDate && { nurseStartDate: nurse.nursingStartingDate }),
	scheduledDeactivationDate: nurse.scheduledDeactivationDate ?? null,
	...(availability.status === "blocked"
		? { unavailableReason: describeUnavailableReason(availability.reason, t) }
		: { badges: buildRowBadges(availability.badges, t) }),
});

/** The cost figures one nurse's prediction resolves to (a salaried nurse never shows overtime). */
export const resolveRowCost = (prediction: CostPredictionResponse | undefined, nurse: Nurse) =>
	buildCostPrediction(prediction, { isGlobalSalary: Boolean(nurse.isGlobalSalary) });

/** Everything a candidate row needs that is the same for every candidate. */
export type CandidateRowContext = Omit<WeekStripArgs, "nurse" | "nurseWeeklyShifts" | "timeOff"> & {
	/** A nurse's time off, from `useNurseTimeOff`. */
	timeOffOf: (nurseId: number) => SpecificPreference[];
	window: ShiftAvailabilityWindow;
	weeks: readonly WeekData[];
	weekIndex: number;
	dayNo: number;
	staffingDate: DateTime;
	breakRulesConfig: BreakRulesConfig[];
	longShiftDurationHours: number;
	predictions: Record<number, CostPredictionResponse>;
	nurseOccurrences: Record<number, number>;
	showActual: boolean;
};

/**
 * The nurse's own per-slot week out of a data-week. Takes the panel's week rather than calling the
 * provider's `getNurseToWeeklyShifts`, which reads the page's week: after the date picker (or a spillover
 * cell) moves the panel to another week, that would show one week's strip and hours beside another's dates.
 */
export const filterNurseWeek = (weeklyShifts: readonly Shift[], nurseId: number): Shift[] =>
	weeklyShifts.map((shift) => ({
		...shift,
		days: shift.days.map((day) => ({
			...day,
			nurses: day.nurses.filter((n) => n.nurseId === nurseId),
			nursesActual: day.nursesActual?.filter((n) => n.nurseId === nurseId),
		})),
	}));

/** One candidate's row, availability included. */
export const buildCandidateRow = (ctx: CandidateRowContext, nurse: Nurse): NurseData => {
	const { window, departmentId, dayStart, staffingDate, weekIndex, dayNo, breakRulesConfig, t } = ctx;
	const nurseWeeklyShifts = filterNurseWeek(ctx.weeklyShifts, nurse.id);
	const timeOff = ctx.timeOffOf(nurse.id);
	const shifts = buildWeekStrip({ ...ctx, nurse, nurseWeeklyShifts, timeOff });
	const cost = resolveRowCost(ctx.predictions[nurse.id], nurse);
	const dailyHours = computeDailyHoursWithShift({ nurse, window, departmentId, breakRulesConfig, nurseWeeklyShifts, dayNo });
	const source: ShiftSource = ctx.showActual ? "actual" : "scheduled";
	const availability = resolveShiftAvailability(nurse, window, {
		departmentId,
		dayStart,
		timeOff,
		staffingDate,
		candidateShifts: collectNurseShiftsAroundDay({ weeks: ctx.weeks, nurseId: nurse.id, weekIndex, dayNo, source }),
		overlapToleranceMs: SHIFT_OVERLAP_TOLERANCE_MS[source],
		// No prediction (wages hidden, still loading) is "unknown", not "no overtime".
		predictedOvertimeHours: cost.overtimeHours ?? null,
		dailyHours,
		longShiftDurationHours: ctx.longShiftDurationHours,
		weeklyShiftOccurrences: ctx.nurseOccurrences,
	});
	return buildNurseRow({ nurse, shifts, cost, dailyHours, availability, t, lastNameFirst: ctx.lastNameFirst });
};
